/**
 * Class for modelling the specific heat of a solution
 */
 // Methods
 // getValue: returns the specific heat capacity.
define([
    './SolventSolutionModeler',
    './SolventConcentrationSolutionModeler',
    './SpecieState',
    './SpecificHeatSolutionMod'
], function (SolventSolutionModeler, SolventConcentrationSolutionModeler, SpecieState,
            SpecificHeatSolutionMod) {
    var instance = null;
    function SpecificHeatSolutionModeler () {
        // The default model used when no other model is selected
        this.DEFAULT = 0;

        // The SOLVENT_REPLACING_SPECIES model is a hybrid model that considers the
        // heat capacity as equal to the heat capacity of the same volume of solvent if
        // there's no replacing species. If there are aqueous species that have
        // volume then evaluates the heat capacity has a sum of the heat capacity of
        // the solvent and that of the replacing species.
        //
        // If NOSOLVENT is the model in SolventSolutionModeler or SOLVENTFINITE is
        // the model for SolventConcentration then the model shifts to ALL_SPECIES.
        this.SOLVENT_REPLACING_SPECIES = 0;

        // The SOLVENT model considers the heat capacity to be equal to the heat
        // capacity of the same volume of solvent
        //
        // If NOSOLVENT is the model in SolventSolutionModeler then the model
        // shifts to ALL_SPECIES.
        this.SOLVENT = 1;

        // The ALL_SPECIES model takes into account the absorbing heat capacities of
        // all the species in the solution
        this.ALL_SPECIES = 2;

        this.SOLVENT2 = 3;

        // Stores the simulation model being used
        this.simulationModel = this.DEFAULT;
    };

    /**
     * Returns the specific heat for the solution
     * @param s The solution
     * @returns {number} The specific heat in cal/g
     */
    SpecificHeatSolutionModeler.prototype.getValue = function (s) {
        // evaluates the energy needed for heating all
        // the solution one K

        if (this.simulationModel === this.SOLVENT2) {
            return SpecificHeatSolutionMod.getValue(s);
        }

        var specificHeat = 0.0;

        // ALL_SPECIES or NOSOLVENT or (SOLVENT_REPLACING_SPECIES and
        // SOLVENTINFINITE)
        if (SolventSolutionModeler.getSimulationModel() === SolventSolutionModeler.NOSOLVENT
            || this.simulationModel === this.ALL_SPECIES
            || (this.simulationModel === this.SOLVENT_REPLACING_SPECIES && SolventConcentrationSolutionModeler
                .simulationModel === SolventConcentrationSolutionModeler.SOLVENTFINITE)) {
            var heatAllSpecies = 0.0;

            var i;
            for (i = 0; i < s.getSpeciesCount(); i++) {
                var sn = s.getSpeciesAt(i);

                if (sn.archetype.id === 0
                    && SolventSolutionModeler.getSimulationModel() !== SolventSolutionModeler.NOSOLVENT) {
                    heatAllSpecies += sn.getWeight()
                        * SolventSolutionModeler.getSolventSpecificHeat();
                }
                else {
                    heatAllSpecies += sn.getWeight()
                        * sn.archetype.cp;
                }
            }

            specificHeat = heatAllSpecies / s.getSolutionWeight();
        }

        // SOLVENTFINITE MODEL
        else if (SolventConcentrationSolutionModeler.simulationModel === SolventConcentrationSolutionModeler.SOLVENTFINITE) {

            // SOLVENT
            if (this.simulationModel === this.SOLVENT) {
                // Model for solvent equivalent in volume

                // heat of solids
                var heatSolids = 0.0;
                for (i = 0; i < s.getSpeciesCount(); i++) {
                    var sn = s.getSpeciesAt(i);

                    if (sn.archetype.state === SpecieState.SOLID) {
                        heatSolids += sn.getWeight()
                            * sn.archetype.cp;
                    }
                }

                // heat for solvent equivalent in volume
                var heatSameVolumeOfSolvent = SolventSolutionModeler
                    .getSolventSpecificHeat()
                    * s.liquidVolume
                    * 1000
                    * SolventSolutionModeler.getSolventDensity();
                specificHeat = (heatSolids + heatSameVolumeOfSolvent) /
                    s.getSolutionWeight();
            }
        }

        // SOLVENTINFINITE MODEL
        else if (SolventConcentrationSolutionModeler.simulationModel === SolventConcentrationSolutionModeler.SOLVENTINFINITE) {

            // SOLVENT
            if (this.simulationModel === this.SOLVENT) {
                // Model for solvent equivalent in volume

                // heat of solids
                var heatSolids = 0.0;
                for (i = 0; i < s.getSpeciesCount(); i++) {
                    var sn = s.getSpeciesAt(i);

                    if (sn.archetype.state === SpecieState.SOLID) {
                        heatSolids += sn.getWeight()
                            * sn.archetype.cp;
                    }
                }

                // heat for solvent equivalent in volume
                var heatSameVolumeOfSolvent = SolventSolutionModeler
                    .getSolventSpecificHeat()
                    * s.liquidVolume
                    * 1000
                    * SolventSolutionModeler.getSolventDensity();
                specificHeat = (heatSolids + heatSameVolumeOfSolvent) /
                        s.getSolventWeight();

                // SOLVENT and REPLACING SPECIES
            }

            // SOLVENT and REPLACING SPECIES
            else if (this.simulationModel === this.SOLVENT_REPLACING_SPECIES) {

                var isThereWaterReplacingSpecies = false;

                // Weight of solvent
                var weight = s.liquidVolume * 1000
                    * SolventSolutionModeler.getSolventDensity();

                // subtract the mass for the mols of water replaced
                var weightWaterReplaced = 0;
                for (i = 0; i < s.getSpeciesCount(); i++) {
                    var sn = s.getSpeciesAt(i);
                    if(sn.archetype.state !== SpecieState.SOLID) {
                        weightWaterReplaced += sn.getWaterReplaced()
                            * SolventSolutionModeler.getSolventMW();
                    }
                }
                if (weightWaterReplaced !== 0) {
                    weight -= weightWaterReplaced;
                    isThereWaterReplacingSpecies = true;
                }

                // so the weight of the solvent is...
                weight = (weight < 0) ? 0 : weight;

                // and heat needed...
                var heatSolvent = weight
                    * SolventSolutionModeler.getSolventSpecificHeat();

                // heat for the rest of species, only if there are solids or
                // water replacing
                var heatSpecies = 0.0;
                var heatSolids = 0.0;

                for (i = 0; i < s.getSpeciesCount(); i++) {
                    var sn = s.getSpeciesAt(i);

                    if (sn.archetype.state === SpecieState.SOLID) {
                        heatSolids += sn.getWeight()
                            * sn.archetype.cp;
                    }
                    if (!(sn.archetype.state === SpecieState.SOLID)
                        && sn.getWaterReplaced() != 0) {
                        heatSpecies += sn.getWeight()
                            * sn.archetype.cp;
                    }
                }

                if (isThereWaterReplacingSpecies) {
                    // Return the specific heat in cal/g/K
                    specificHeat = (heatSolvent + heatSolids + heatSpecies) /
                        s.getSolutionWeight();
                }
                else {
                    // Model for solvent equivalent in volume
                    var heatSameVolumeOfSolvent = SolventSolutionModeler
                        .getSolventSpecificHeat()
                        * s.liquidVolume
                        * 1000
                        * SolventSolutionModeler.getSolventDensity();

                    var solutionWeight = s.getSolutionWeight();
                    if (solutionWeight !== 0) {
                        specificHeat = (heatSolids + heatSameVolumeOfSolvent) /
                            solutionWeight;
                    }
                    else {
                        specificHeat = 0;
                    }
                }
            }
        }
        return specificHeat;
    };

    /**
     * Resets the default status
     */
    SpecificHeatSolutionModeler.prototype.setDefault = function () {
        this.simulationModel = this.DEFAULT;
    };

    /**
     * Load the characteristics of the model from the problem file
     * @param model value of solutionModellers.specificHeat from configuration
     */
    SpecificHeatSolutionModeler.prototype.loadProperties = function(model) {
        var modelUpper = model.toUpperCase();
        if (modelUpper === "DEFAULT")
            this.simulationModel = this.DEFAULT;
        else if (modelUpper === "SOLVENT")
            this.simulationModel = this.SOLVENT;
        else if (modelUpper === "ALL")
            this.simulationModel = this.ALL_SPECIES;
        else if (modelUpper === "REPLACING")
            this.simulationModel = this.SOLVENT_REPLACING_SPECIES;
        else if (modelUpper === "SOLVENT2") {
            this.simulationModel = this.SOLVENT2;
        }
    };

    SpecificHeatSolutionModeler.getInstance = function() {
        if(instance === null) {
            instance = new SpecificHeatSolutionModeler();
        }
        return instance;
    };
    return SpecificHeatSolutionModeler.getInstance();
});
